// services/qualityCheck.ts — SKIN-239 / SKIN-262 (epic SKIN-230, Quality Check)
import axios, { CancelToken } from 'axios';
import { QcQueueRow, QcQueueState } from '../pages/hospital/QualityCheck/VisitQueue';
import { QcCategorySection } from '../pages/hospital/QualityCheck/qcCategories';
import { QcOutcome } from '../pages/hospital/QualityCheck/imageMeta';
import { isFlagView } from '../pages/hospital/QualityCheck/correctionFlag';
import type { CorrectionFlagView, FlagStep } from '../pages/hospital/QualityCheck/correctionFlag';

// The QC queue and visit review hang off the visit router (/visits), NOT /analytics.
const VISITS_BASE_URL = import.meta.env.VITE_APP_API_URL + '/visits';
const ANALYTICS_BASE_URL = import.meta.env.VITE_APP_API_URL + '/analytics';

// `?? ''` keeps the header a string: axios's config type rejects `string | null`.
const getAuthHeaders = () => ({ headers: { Authorization: localStorage.getItem('jwt') ?? '' } });

const QC_QUEUE_STATES: QcQueueState[] = ['submitted', 'in_review', 'approved'];

/** One correction on the identifiable audit trail (SKIN-237 / SKIN-256). */
export interface QcIdentifiableCorrectionDto {
    from: boolean | null;
    to: boolean | null;
    at: string;
    actor: { uuid: string; name: string | null; surnames: string | null } | null;
    reason: string;
}

/**
 * SKIN-637 — a visit shot the dermatologist recorded as not possible. `reason` is the
 * chosen option's label in the study's language; `note` the "Other" text. Either may be
 * null.
 */
export interface QcVisitShotSkipDto {
    reason: string | null;
    note: string | null;
}

/**
 * SKIN-618 — a required shot taken once per visit, outside any lesion (SKIN-41's
 * healthy skin). Checked for presence only; `localIds` empty means it is missing unless
 * it is `skipped` (SKIN-637). An API older than SKIN-637 sends no `skipped`.
 */
export interface QcVisitShotDto {
    fieldName: string;
    localIds: string[];
    skipped?: QcVisitShotSkipDto | null;
}

export interface QcImageStateDto {
    encryptedFileUuid: string;
    localId: string;
    imageFieldName: string | null;
    groupRecordUuid: string | null;
    qualityScore: number | null;
    identifiable: boolean | null;
    autoIdentifiable: boolean | null;
    entry: { outcome: string | null; comment: string | null; categoryIds: number[] } | null;
    corrections: QcIdentifiableCorrectionDto[];
}

/** SKIN-611 — an ICD-11 code and its disease name: the stored label, else the WHO catalogue title (SKIN-612); null only when nothing names it. */
export interface QcDiagnosisCodeDto {
    code: string;
    label: string | null;
}

/**
 * SKIN-611 — one diagnostic workup as the API serves it (`QcDiagnosticWorkup` in
 * sherwood-api `qc-feedback/diagnosticWorkup.ts`): per lesion since SKIN-444, or the
 * legacy visit-level D1. Option answers arrive as their labels. Read-only.
 */
export interface QcDiagnosticWorkupDto {
    clinicalDiagnoses: QcDiagnosisCodeDto[];
    otherDiagnosisText: string | null;
    confidence: string | null;
    differentialDiagnoses: QcDiagnosisCodeDto[];
    labTestPerformed: string | null;
    labTestType: string | null;
    labResultDiagnoses: QcDiagnosisCodeDto[];
    finalDiagnoses: QcDiagnosisCodeDto[];
    labId: string | null;
}

export interface QcLesionStateDto {
    groupRecordUuid: string;
    bodyRegion: string | null;
    target: string | null;
    /** SKIN-441 — whether this lesion instance carries its own `lesion_diagnoses` answer. */
    diagnosed: boolean;
    /**
     * SKIN-611 — the workup recorded inside this lesion instance; null when it answered
     * none of it. Optional only because an API older than SKIN-611 does not send it.
     */
    diagnosticWorkup?: QcDiagnosticWorkupDto | null;
    entry: { comment: string | null; categoryIds: number[] } | null;
}

/** Shape of GET …/visit/:uuidVisit/qc (SKIN-256). */
export interface QcVisitStateDto {
    state: QcQueueState;
    images: QcImageStateDto[];
    /** SKIN-618 — the required visit-level shots, present or missing. Absent from an API older than SKIN-618. */
    requiredVisitShots?: QcVisitShotDto[];
    lesions: QcLesionStateDto[];
    /**
     * SKIN-441 — the same counts the queue row carries, served here so the detail
     * screen states one number rather than letting the reviewer compare a badge
     * against the chips they can see. NOT `lesions.length`: see the API's QcVisitState.
     */
    lesionCount: number;
    undiagnosedLesionCount: number;
    /**
     * SKIN-611 — the legacy visit-level D1, one workup per instance, from the canonical
     * submission. Describes the visit; never attributed to a lesion. Empty on a visit
     * recorded per lesion; absent from an API older than SKIN-611.
     */
    visitLevelDiagnoses?: QcDiagnosticWorkupDto[];
    lesionCategorySections: QcCategorySection[];
    /** SKIN-577 — the reasons a reviewer may give for flagging the visit (the QC Survey's VISIT section). */
    visitCategorySections: QcCategorySection[];
    /** SKIN-577 — the visit's correction flag: its current snapshot and every step. */
    flag: CorrectionFlagView;
    /** SKIN-577 — the enrolment the visit belongs to, for the link to the patient record. */
    patientInvestigationUuid: string;
}

export type UpsertImageQcBody = {
    outcome: QcOutcome;
    categoryIds?: number[];
    comment?: string;
};

export type UpsertLesionQcBody = {
    categoryIds?: number[];
    comment?: string;
};

const visitQcBase = (uuidInvestigation: string, uuidVisit: string) =>
    `${VISITS_BASE_URL}/investigation/${uuidInvestigation}/visit/${uuidVisit}`;

/**
 * SKIN-577 — the body of each step on the correction flag. OPEN carries the reasons, and a note
 * only with the "Other" reason, where the API requires it; resolve and reopen carry a note,
 * which the API requires and trims. CORRECTED (SKIN-600, the assignee's) takes an optional note.
 */
export type CorrectionFlagStepBody =
    | { reasonFieldIds: number[]; note?: string }
    | { note: string };

/** The body a step takes: the reviewer's need a note; CORRECTED's is optional. */
type FlagStepBody<S extends FlagStep> = S extends 'CORRECTED' ? { note?: string } : CorrectionFlagStepBody;

/** Where each step is posted, under `…/visit/:uuidVisit/qc/flag`. */
const FLAG_STEP_SUFFIX: Record<FlagStep, string> = {
    OPEN: '',
    RESOLVE: '/resolve',
    REOPEN: '/reopen',
    CORRECTED: '/corrected',
};

/** SKIN-600 — one open correction flag (OPEN or CORRECTED) on a SUBMITTED visit. */
export interface OpenCorrection {
    visitUuid: string;
    /** The patient page's `:uuidPatient`. */
    patientInvestigationUuid: string;
    flag: CorrectionFlagView;
}

export interface OpenCorrections {
    flags: OpenCorrection[];
    /** The study's visit-level reasons, to label `reasonFieldIds` (the dermatologist cannot read QC). */
    visitReasonSections: QcCategorySection[];
}

/**
 * SKIN-441 — the API is older than this client and is not serving the lesion counts.
 *
 * The message is what a reviewer sees: `ErrorState` renders `error.message` verbatim,
 * so the wire diagnostic cannot double as the copy. A clinician being shown a Jira key
 * learns nothing and cannot act on it; the engineer needs the key and gets it in the
 * console, where the person who can act on it is looking.
 */
function staleApiError(detail: string): Error {
    // eslint-disable-next-line no-console
    console.error(`[quality-check] ${detail} — the API is older than this client.`);
    return new Error('Quality Check is temporarily unavailable. Please try again later.');
}

export const qualityCheckServices = {
    // GET /visits/investigation/:uuidInvestigation/qc/queue
    // See SKIN-239 notes: unfiltered fetch; VisitQueue filters locally; CancelToken only.
    getQcQueue: async (
        uuidInvestigation: string,
        cancelToken?: CancelToken,
    ): Promise<QcQueueRow[]> => {
        const url = `${VISITS_BASE_URL}/investigation/${uuidInvestigation}/qc/queue`;
        const response = await axios.get(url, { ...getAuthHeaders(), cancelToken });
        if (response.status !== 200) throw response.data;
        const rows = response.data?.rows;
        if (!Array.isArray(rows)) throw new Error('QC queue response has no rows array');
        const unrecognised = rows.find((row: QcQueueRow) => !QC_QUEUE_STATES.includes(row?.state));
        if (unrecognised) {
            throw new Error(`QC queue row has an unrecognised state: ${unrecognised.state}`);
        }
        // SKIN-441 — the two count fields are validated for the same reason `state` is:
        // the client and the API deploy separately, so a client that has shipped this
        // ticket can meet an API that has not. Without the check `undefined > 0` is
        // false, and the screen renders an empty Lesions column with a diagnosis filter
        // that silently matches nothing — a wrong answer with no error anywhere, which
        // is worse than the error state the queue already knows how to show.
        const uncounted = rows.find((row: QcQueueRow) => (
            !Number.isFinite(row?.lesionCount) || !Number.isFinite(row?.undiagnosedLesionCount)
        ));
        if (uncounted) {
            throw staleApiError('QC queue rows are missing their lesion counts (SKIN-441)');
        }
        // SKIN-577 — null is "no open flag"; a missing key is an older API, and reading it
        // as null would leave the Needs-correction filter matching nothing, with no error.
        if (rows.some((row: QcQueueRow) => row?.openCorrectionFlag === undefined)) {
            throw staleApiError('QC queue rows are missing openCorrectionFlag (SKIN-577)');
        }
        return rows;
    },

    // GET /visits/investigation/:inv/visit/:visit/qc — SKIN-256 visit review payload.
    getVisitQcState: async (
        uuidInvestigation: string,
        uuidVisit: string,
        cancelToken?: CancelToken,
    ): Promise<QcVisitStateDto> => {
        const url = `${visitQcBase(uuidInvestigation, uuidVisit)}/qc`;
        const response = await axios.get(url, { ...getAuthHeaders(), cancelToken });
        if (response.status !== 200) throw response.data;
        const {
            state, images, lesions, lesionCategorySections, lesionCount, undiagnosedLesionCount,
            visitCategorySections, flag, patientInvestigationUuid, requiredVisitShots, visitLevelDiagnoses,
        } = response.data ?? {};
        if (!QC_QUEUE_STATES.includes(state)) {
            throw new Error(`QC visit state has an unrecognised state: ${state}`);
        }
        if (!Array.isArray(images)) throw new Error('QC visit response has no images array');
        if (!Array.isArray(lesions)) throw new Error('QC visit response has no lesions array');
        if (!Array.isArray(lesionCategorySections)) {
            throw new Error('QC visit response has no lesionCategorySections array');
        }
        // SKIN-441 — checked rather than defaulted to 0. A missing count is an API older
        // than this client, and defaulting would render "no lesions are undiagnosed" on a
        // visit where some are: a wrong clinical answer, stated confidently. This
        // response is rebuilt field by field rather than spread, so a new field that is
        // not named here is silently dropped — which is how this one was nearly lost.
        if (!Number.isFinite(lesionCount) || !Number.isFinite(undiagnosedLesionCount)) {
            throw staleApiError('QC visit response is missing its lesion counts (SKIN-441)');
        }
        // SKIN-577 — the same rule. A missing flag defaulted to "never flagged" would offer
        // Approve on a visit the API is about to refuse, and hide the flag that explains why.
        if (!isFlagView(flag) || !Array.isArray(visitCategorySections) || typeof patientInvestigationUuid !== 'string') {
            throw staleApiError('QC visit response is missing its correction flag (SKIN-577)');
        }
        return {
            state, images, lesions, lesionCategorySections, lesionCount, undiagnosedLesionCount,
            visitCategorySections, flag, patientInvestigationUuid,
            // SKIN-641 — optional on purpose (absent from an API older than SKIN-611/618), but
            // named here: this return drops whatever it does not name, which hid the
            // healthy-skin panel and the visit-level diagnosis block.
            requiredVisitShots, visitLevelDiagnoses,
        };
    },

    // GET /analytics/:inv/clinical/qc-feedback-categories?imageFieldName=…
    // Envelope is `{ status, sections }` (not `{ data }`). Cache by field name in the container.
    getQcFeedbackCategories: async (
        uuidInvestigation: string,
        imageFieldName: string,
        cancelToken?: CancelToken,
    ): Promise<QcCategorySection[]> => {
        const url = `${ANALYTICS_BASE_URL}/${uuidInvestigation}/clinical/qc-feedback-categories`;
        const response = await axios.get(url, {
            ...getAuthHeaders(),
            cancelToken,
            params: { imageFieldName },
        });
        if (response.status !== 200) throw response.data;
        const sections = response.data?.sections;
        if (!Array.isArray(sections)) throw new Error('QC categories response has no sections array');
        return sections;
    },

    // PUT …/qc/image/:encryptedFileUuid
    upsertImageQcEntry: async (
        uuidInvestigation: string,
        uuidVisit: string,
        encryptedFileUuid: string,
        body: UpsertImageQcBody,
    ): Promise<void> => {
        const url = `${visitQcBase(uuidInvestigation, uuidVisit)}/qc/image/${encryptedFileUuid}`;
        const response = await axios.put(url, body, getAuthHeaders());
        if (response.status !== 200) throw response.data;
    },

    // PUT …/qc/lesion/:groupRecordUuid
    upsertLesionQcEntry: async (
        uuidInvestigation: string,
        uuidVisit: string,
        groupRecordUuid: string,
        body: UpsertLesionQcBody,
    ): Promise<void> => {
        const url = `${visitQcBase(uuidInvestigation, uuidVisit)}/qc/lesion/${groupRecordUuid}`;
        const response = await axios.put(url, body, getAuthHeaders());
        if (response.status !== 200) throw response.data;
    },

    /**
     * PATCH …/qc/image/:uuid/identifiable — the audited correction (SKIN-237).
     *
     * The field is `identifiable`, named as the API names it. It shipped here as `to`,
     * matching the READ's correction shape (`{ from, to, … }`) rather than the WRITE's,
     * and since nothing called it until SKIN-243 nothing found out: the endpoint refuses
     * any body whose `identifiable` is not a boolean, so every correction a reviewer made
     * would have failed with a 400 the client would have shown as "could not be saved".
     */
    correctIdentifiable: async (
        uuidInvestigation: string,
        uuidVisit: string,
        encryptedFileUuid: string,
        body: { identifiable: boolean; reason: string },
    ): Promise<void> => {
        const url = `${visitQcBase(uuidInvestigation, uuidVisit)}/qc/image/${encryptedFileUuid}/identifiable`;
        const response = await axios.patch(url, body, getAuthHeaders());
        if (response.status !== 200) throw response.data;
    },

    // PATCH …/approve — service only until SKIN-242 wires the rail.
    approveVisit: async (uuidInvestigation: string, uuidVisit: string): Promise<void> => {
        const url = `${visitQcBase(uuidInvestigation, uuidVisit)}/approve`;
        const response = await axios.patch(url, {}, getAuthHeaders());
        if (response.status !== 200) throw response.data;
    },

    /**
     * GET /visits/investigation/:inv/corrections — SKIN-600, the caller's open correction flags;
     * a SUPER_ADMIN gets the whole study's (sherwood-api `correction-permission.md`). The
     * study-wide view filters by assignee on this one answer rather than asking `?assigneeUuid=`,
     * so its filter always lists every assignee there is.
     */
    listOpenCorrections: async (uuidInvestigation: string): Promise<OpenCorrections> => {
        const response = await axios.get(`${VISITS_BASE_URL}/investigation/${uuidInvestigation}/corrections`, getAuthHeaders());
        if (response.status !== 200) throw response.data;
        const { flags, visitReasonSections } = response.data ?? {};
        // A row without its flag would read as "never flagged": an API older than this client.
        if (!Array.isArray(flags) || !Array.isArray(visitReasonSections)
            || !flags.every((row: OpenCorrection) => isFlagView(row?.flag))) {
            // Not staleApiError: its copy names Quality Check, which a dermatologist does not have.
            throw new Error('Corrections response is missing its flags (SKIN-599): the API is older than this client');
        }
        return { flags, visitReasonSections };
    },

    /**
     * POST …/qc/flag[/resolve|/reopen|/corrected] — SKIN-577, one step on the correction flag
     * (SKIN-600: CORRECTED, the assignee's).
     *
     * Answers the flag as the server recorded it after the step, which the screen folds in:
     * the history names the actor, and only the server can say who that was.
     */
    applyCorrectionFlagStep: async <S extends FlagStep>(
        uuidInvestigation: string,
        uuidVisit: string,
        step: S,
        body: FlagStepBody<S>,
    ): Promise<CorrectionFlagView> => {
        const url = `${visitQcBase(uuidInvestigation, uuidVisit)}/qc/flag${FLAG_STEP_SUFFIX[step]}`;
        const response = await axios.post(url, body, getAuthHeaders());
        if (response.status !== 200) throw response.data;
        const flag = response.data?.flag;
        if (!isFlagView(flag)) throw new Error('Correction flag response has no flag');
        return flag;
    },

    // PATCH …/void-approval — service only until SKIN-242 wires the rail.
    voidVisitApproval: async (uuidInvestigation: string, uuidVisit: string): Promise<void> => {
        const url = `${visitQcBase(uuidInvestigation, uuidVisit)}/void-approval`;
        const response = await axios.patch(url, {}, getAuthHeaders());
        if (response.status !== 200) throw response.data;
    },
};
